/**
 * PURPOSE: Reglas puras de elegibles Rugby en Winscore (FTR-0015).
 * RESPONSIBILITIES: Etiquetas (perfiles, modos, aplicación, estados de envío, bloqueos del estado actual, advertencias);
 *   textos del plazo (sin configuración base = envío cerrado, sin kickoff, vencido), del estado de la regla (sin
 *   confirmar = Sevens 7/12 en advertencia solo como vista previa) y de la configuración mínima del delegado (regla
 *   confirmada + plazo/cambios del deporte; decisión del propietario del 2026-09-11); formularios de regla y de
 *   plazo/cambios con los mismos límites del backend; fechas numeradas del fixture agrupadas por su identidad
 *   canónica (`nroFecha`, nunca la fecha calendario) con el modo efectivo por fecha — fijo o relativo — para el
 *   panel de plazo de envío (corrección del propietario del 2026-09-11); edición administrativa de la lista (filas,
 *   validación de camisetas, entradas); clave de idempotencia; mensajes por código de error.
 * COLLABORATORS: CompanyRugbyLineupsTab.jsx, RugbyLineupRulePanel.jsx, RugbyLineupPolicyPanel.jsx,
 *   RugbyFixtureDeadlinePanel.jsx, RugbyLineupSidePanel.jsx, auditDisplay.utils.js.
 * ROUTE: winscore/src/admin/AdminCompany/rugbyLineups.utils.js
 */
import { formatLimaDateTime } from "./uniformOptions.utils.js";

export const RULE_PROFILE_LABELS = Object.freeze({ SEVENS: "Sevens", XV: "XV", M18: "M18", CUSTOM: "Personalizado" });
export const COUNT_MODE_LABELS = Object.freeze({ AT_MOST: "Como máximo", EXACT: "Exacto" });
export const ENFORCEMENT_LABELS = Object.freeze({ WARN: "Advertencia", BLOCK: "Bloqueo" });
export const EXHAUSTION_LABELS = Object.freeze({ BLOCK: "Bloquear", ALLOW_WITH_WARNING: "Permitir con advertencia" });
export const RULE_LIMITS = Object.freeze({ STARTER_MIN: 1, STARTER_MAX: 30, ELIGIBLE_MIN: 1, ELIGIBLE_MAX: 60 });
export const POLICY_LIMITS = Object.freeze({ MINUTES_MAX: 10080, ALLOWED_CHANGES_MAX: 20, GRANT_MAX: 20 });
export const FALLBACK_RULE_TEXT = "Sevens 7 titulares / 12 elegibles en advertencia";

export const OFFICIAL_STATE_LABELS = Object.freeze({
  NOT_SUBMITTED: { text: "Sin envío", tone: "slate" },
  IN_OFFICIAL_SNAPSHOT: { text: "En la alineación oficial", tone: "emerald" },
  PENDING_OTHER_SIDE: { text: "Confirmado · falta el rival", tone: "amber" },
  SUPERSEDED_BY_PLANILLA: { text: "Reemplazado por la planilla", tone: "rose" },
});

// Motivos derivados del estado ACTUAL en cada lectura: volver a Habilitado retira solo el del estado.
export const LOCK_REASON_LABELS = Object.freeze({
  LINEUP_CONFIGURATION_PENDING: "Configuración pendiente",
  MATCH_CLOSED: "Partido cerrado",
  MATCH_LIVE_LOCKED: "Partido en vivo",
  MATCH_NOT_ENABLED: "Partido no habilitado",
  DEADLINE_PASSED: "Plazo vencido",
  ALLOWANCE_EXHAUSTED: "Sin cambios disponibles",
  EXCESS_ACK_REQUIRED: "Cambio adicional con advertencia",
});

export const DEADLINE_WARNING_LABELS = Object.freeze({
  KICKOFF_MISSING: "Falta fecha u hora de inicio: define un plazo del partido.",
  OVERRIDE_WITHOUT_BASE: "Hay un plazo propio, pero no rige hasta guardar el plazo y los cambios del deporte.",
  RESCHEDULED_AFTER_OVERRIDE: "El partido se reprogramó después de fijar su plazo absoluto (el plazo se mantiene).",
  KICKOFF_PASSED_NOT_STARTED: "Pasó la hora de inicio y el partido no se inició: la alineación sigue oculta.",
});

export const CONFIGURATION_MISSING_LABELS = Object.freeze({
  RULE_NOT_CONFIRMED: "confirmar la regla",
  BASE_POLICY_MISSING: "guardar el plazo y los cambios del deporte",
});

/** Configuración mínima para que los delegados envíen: regla confirmada + plazo/cambios del evento-deporte. */
export const describeSubmissionConfiguration = (configuration) => {
  if (configuration?.status === "READY") return { tone: "emerald", text: "Configuración completa" };
  const missing = (configuration?.missing || []).map((code) => CONFIGURATION_MISSING_LABELS[code] || code);
  return {
    tone: "amber",
    text: `Configuración pendiente${missing.length ? ` · falta ${missing.join(" y ")}` : ""}`,
  };
};

// ------------------------------ Plazo de envío: fechas del fixture ------------------------------
export const FIXTURE_DEADLINE_MODES = Object.freeze({ RELATIVE: "RELATIVE", FIXED: "FIXED" });
export const FIXTURE_DEADLINE_MODE_LABELS = Object.freeze({
  RELATIVE: "Cerrar antes de cada partido",
  FIXED: "Usar una fecha y hora común para esta fecha",
});

/**
 * Agrupa los partidos del panorama por su fecha numerada del fixture (identidad canónica; una fecha calendario no
 * cuenta como fecha propia — decisión del propietario del 2026-09-11). Partidos sin `nroFecha` quedan fuera: se
 * gestionan con el control individual "Plazo del partido" de la tabla, nunca con una entrada "Día DD/MM/YYYY" aquí.
 * El modo efectivo de una fecha es fijo cuando al menos uno de sus partidos abiertos tiene un plazo propio (override
 * de alcance MATCH) activo; `mixed` avisa cuando no todos comparten el mismo modo, para no mostrarlo como limpio.
 */
export const buildFixtureRounds = (matches = []) => {
  const byRound = new Map();
  for (const match of matches) {
    if (match?.nroFecha === null || match?.nroFecha === undefined) continue;
    if (!byRound.has(match.nroFecha)) byRound.set(match.nroFecha, []);
    byRound.get(match.nroFecha).push(match);
  }
  return [...byRound.entries()]
    .sort(([left], [right]) => left - right)
    .map(([nroFecha, roundMatches]) => {
      const openMatches = roundMatches.filter((match) => !match.closed);
      const fixedMatches = openMatches.filter((match) => match.override?.scopeType === "MATCH");
      const mode = fixedMatches.length > 0 ? FIXTURE_DEADLINE_MODES.FIXED : FIXTURE_DEADLINE_MODES.RELATIVE;
      return {
        nroFecha,
        matchCount: roundMatches.length,
        openCount: openMatches.length,
        fixedCount: fixedMatches.length,
        mode,
        mixed: mode === FIXTURE_DEADLINE_MODES.FIXED && fixedMatches.length < openMatches.length,
        closesAtLima: fixedMatches[0]?.override?.closesAtLima || null,
      };
    });
};

/** Compacto: qué rige hoy en una fecha y cuántos partidos abarca (para el selector y el estado leído). */
export const describeFixtureRound = (round) => {
  if (!round) return { tone: "slate", text: "" };
  if (round.mode === FIXTURE_DEADLINE_MODES.RELATIVE) {
    return { tone: "slate", text: `${FIXTURE_DEADLINE_MODE_LABELS.RELATIVE} · ${round.openCount} partido(s) abierto(s)` };
  }
  const when = round.closesAtLima ? `${formatLimaDateTime(round.closesAtLima)} (Lima)` : "";
  const coverage = round.mixed ? `${round.fixedCount} de ${round.openCount} partidos` : `${round.fixedCount} partido(s)`;
  return { tone: "emerald", text: `${FIXTURE_DEADLINE_MODE_LABELS.FIXED} · ${when} · ${coverage}` };
};

/** Descripciones fijas de auditoría que el portal envía siempre: el administrador nunca escribe un motivo aquí. */
export const FIXTURE_DEADLINE_REASONS = Object.freeze({
  FIXED: "Cambio de fecha y hora límite de jornada desde Administración General",
  RELATIVE: "Restablecimiento del plazo previo al partido desde Administración General",
});

export const RULE_WARNING_LABELS = Object.freeze({
  STARTER_LIMIT_EXCEEDED: "Titulares por encima del máximo",
  STARTER_COUNT_MISMATCH: "Titulares distintos a los exigidos",
  ELIGIBLE_LIMIT_EXCEEDED: "Elegibles por encima del máximo",
  ALLOWANCE_EXCEEDED: "Cambio por encima de lo permitido",
});

export const describeRugbyDeadline = (deadline) => {
  if (!deadline) return { tone: "slate", text: "Sin información de plazo" };
  const when = deadline.closesAtLima ? `${formatLimaDateTime(deadline.closesAtLima)} (Lima)` : "";
  if (deadline.reason === "NOT_CONFIGURED") return { tone: "amber", text: "Sin plazo del deporte · envío de delegados cerrado" };
  if (deadline.status === "CLOSED") return { tone: "rose", text: `Plazo vencido · cerró ${when}` };
  if (deadline.reason === "KICKOFF_MISSING") return { tone: "amber", text: "Sin hora de inicio · abierto (configura un plazo del partido)" };
  const source = deadline.source === "MATCH_INSTITUTION" ? " · extensión" : deadline.source === "MATCH" ? " · plazo del partido" : "";
  return { tone: "emerald", text: `Abierto hasta ${when}${source}` };
};

export const describeRuleStatus = (ruleStatus) => {
  if (ruleStatus?.status === "CONFIRMED") {
    const rule = ruleStatus.effective || {};
    return {
      tone: "emerald",
      text: `Confirmada · ${RULE_PROFILE_LABELS[rule.profileKey] || rule.profileKey} ${rule.starterLimit}/${rule.eligibleMax}`,
    };
  }
  // El fallback es solo vista previa: sin regla confirmada los delegados no envían.
  const preview = `vista previa ${FALLBACK_RULE_TEXT}; los delegados no envían hasta confirmar`;
  if (ruleStatus?.status === "UNCONFIRMED") return { tone: "amber", text: `Sin confirmar · ${preview}` };
  return { tone: "amber", text: `Sin configurar · ${preview}` };
};

// ------------------------------------------ Regla ------------------------------------------
export const ruleFormFrom = (ruleStatus, profiles = []) => {
  const configured = ruleStatus?.configured;
  if (configured) {
    return {
      profileKey: configured.profileKey,
      starterLimit: String(configured.starterLimit),
      eligibleMax: String(configured.eligibleMax),
      starterCountMode: configured.starterCountMode,
      starterEnforcement: configured.starterEnforcement,
      eligibleEnforcement: configured.eligibleEnforcement,
    };
  }
  const sevens = profiles.find((profile) => profile.key === "SEVENS") || { starterLimit: 7, eligibleMax: 12 };
  return {
    profileKey: "SEVENS",
    starterLimit: String(sevens.starterLimit),
    eligibleMax: String(sevens.eligibleMax),
    starterCountMode: "AT_MOST",
    starterEnforcement: "BLOCK",
    eligibleEnforcement: "BLOCK",
  };
};

/** Al elegir un perfil sembrado se copian sus límites; CUSTOM conserva los actuales para editarlos. */
export const applyProfile = (form, profileKey, profiles = []) => {
  const profile = profiles.find((item) => item.key === profileKey);
  if (!profile) return { ...form, profileKey };
  return { ...form, profileKey, starterLimit: String(profile.starterLimit), eligibleMax: String(profile.eligibleMax) };
};

const integerIn = (value, min, max) => {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed >= min && parsed <= max;
};

export const validateRuleForm = (form) => {
  const errors = {};
  if (!integerIn(form.starterLimit, RULE_LIMITS.STARTER_MIN, RULE_LIMITS.STARTER_MAX)) {
    errors.starterLimit = `Entero entre ${RULE_LIMITS.STARTER_MIN} y ${RULE_LIMITS.STARTER_MAX}.`;
  }
  if (!integerIn(form.eligibleMax, RULE_LIMITS.ELIGIBLE_MIN, RULE_LIMITS.ELIGIBLE_MAX)) {
    errors.eligibleMax = `Entero entre ${RULE_LIMITS.ELIGIBLE_MIN} y ${RULE_LIMITS.ELIGIBLE_MAX}.`;
  }
  if (!errors.starterLimit && !errors.eligibleMax && Number(form.starterLimit) > Number(form.eligibleMax)) {
    errors.starterLimit = "No puede superar el máximo de elegibles.";
  }
  return errors;
};

export const buildRulePayload = (form, { confirm }) => ({
  profileKey: form.profileKey,
  starterLimit: Number(form.starterLimit),
  eligibleMax: Number(form.eligibleMax),
  starterCountMode: form.starterCountMode,
  starterEnforcement: form.starterEnforcement,
  eligibleEnforcement: form.eligibleEnforcement,
  confirm: Boolean(confirm),
});

// ------------------------------------ Plazo y cambios ------------------------------------
export const policyFormFrom = (policy) => ({
  minutesBeforeKickoff: policy?.minutesBeforeKickoff !== undefined && policy?.minutesBeforeKickoff !== null ? String(policy.minutesBeforeKickoff) : "120",
  allowedChanges: policy?.allowedChanges !== undefined && policy?.allowedChanges !== null ? String(policy.allowedChanges) : "0",
  exhaustionBehavior: policy?.exhaustionBehavior || "BLOCK",
});

export const validatePolicyForm = (form) => {
  const errors = {};
  if (!integerIn(form.minutesBeforeKickoff, 0, POLICY_LIMITS.MINUTES_MAX)) errors.minutesBeforeKickoff = `Entero entre 0 y ${POLICY_LIMITS.MINUTES_MAX}.`;
  if (!integerIn(form.allowedChanges, 0, POLICY_LIMITS.ALLOWED_CHANGES_MAX)) errors.allowedChanges = `Entero entre 0 y ${POLICY_LIMITS.ALLOWED_CHANGES_MAX}.`;
  if (!EXHAUSTION_LABELS[form.exhaustionBehavior]) errors.exhaustionBehavior = "Elige un comportamiento.";
  return errors;
};

export const buildPolicyPayload = (form) => ({
  minutesBeforeKickoff: Number(form.minutesBeforeKickoff),
  allowedChanges: Number(form.allowedChanges),
  exhaustionBehavior: form.exhaustionBehavior,
});

// ------------------------------ Edición administrativa de la lista ------------------------------
/** Filas editables: todas las acreditaciones elegibles del lado, marcadas según la versión vigente. */
export const rosterRowsFromState = (state) => {
  const current = new Map((state?.current?.entries || []).map((entry) => [Number(entry.idacreditation), entry]));
  return (state?.accreditations || []).map((item) => {
    const entry = current.get(Number(item.idacreditation));
    return {
      idacreditation: Number(item.idacreditation),
      name: item.name,
      selected: Boolean(entry),
      role: entry?.role || "SUPLENTE",
      jersey: entry ? String(entry.jersey ?? "") : String(item.defaultJersey || ""),
    };
  });
};

const jerseyKey = (value) => {
  const text = String(value ?? "").trim();
  return /^\d{1,3}$/.test(text) ? String(Number(text)) : text;
};

export const summarizeRoster = (rows) => {
  const selected = rows.filter((row) => row.selected);
  const counts = new Map();
  selected.forEach((row) => {
    const key = jerseyKey(row.jersey);
    if (key) counts.set(key, (counts.get(key) || 0) + 1);
  });
  return {
    eligibles: selected.length,
    starters: selected.filter((row) => row.role === "TITULAR").length,
    duplicateJerseys: [...counts.entries()].filter(([, total]) => total > 1).map(([key]) => key),
    missingJersey: selected.filter((row) => !/^\d{1,3}$/.test(String(row.jersey ?? "").trim())).map((row) => row.idacreditation),
  };
};

export const entriesFromRows = (rows) =>
  rows
    .filter((row) => row.selected)
    .map((row) => ({ idacreditation: row.idacreditation, role: row.role === "TITULAR" ? "TITULAR" : "SUPLENTE", jersey: String(row.jersey).trim() }));

/** UUID v4 para la idempotencia del guardado confirmado (reutilizar en reintentos del mismo pedido). */
export const newIdempotencyKey = (cryptoApi = typeof window !== "undefined" ? window.crypto : undefined) => {
  if (cryptoApi?.randomUUID) return cryptoApi.randomUUID();
  const bytes = new Uint8Array(16);
  cryptoApi.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = [...bytes].map((byte) => byte.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

// ------------------------------------------ Errores ------------------------------------------
const ERROR_MESSAGES = Object.freeze({
  RUGBY_LINEUPS_DISABLED: "Los elegibles Rugby no están habilitados en este entorno.",
  REASON_REQUIRED: "Indica el motivo (obligatorio para esta acción).",
  VERSION_CONFLICT: "Hay una versión confirmada más reciente. Recarga el lado antes de guardar.",
  IDEMPOTENCY_KEY_REUSED: "Ese guardado ya se procesó con otros datos. Recarga e intenta de nuevo.",
  RULE_VIOLATION: "La lista no cumple la regla confirmada del evento.",
  INVALID_ACCREDITATION: "Hay jugadores que no están acreditados y aprobados para este equipo.",
  DUPLICATE_JERSEY: "Hay números de camiseta repetidos.",
  JERSEY_REQUIRED: "Cada jugador elegible requiere número de camiseta.",
  INVALID_JERSEY: "La camiseta debe tener de 1 a 3 dígitos.",
  EMPTY_LINEUP: "Confirma al menos un jugador elegible.",
  MATCH_CLOSED: "El partido ya no admite cambios de elegibles.",
  MATCH_SIDES_AMBIGUOUS: "Ambos lados del partido pertenecen a la misma institución.",
  INVALID_DEADLINE: "Ingresa fecha, hora y minuto válidos (hora de Lima).",
  INVALID_RULE: "Revisa los límites de la regla.",
  INVALID_POLICY: "Revisa los minutos y los cambios permitidos.",
  INVALID_GRANT: "Los cambios extra deben ser un entero entre 1 y 20.",
  INVALID_SELECTOR: "Elige una fecha del fixture o una fecha calendario.",
  NO_MATCHES: "No hay partidos abiertos en esa fecha.",
  POLICY_NOT_FOUND: "No hay un plazo activo para retirar.",
  MATCH_NOT_STARTABLE: "El partido solo se puede iniciar desde el estado Habilitado.",
  LINEUP_CONFIGURATION_PENDING: "El envío de elegibles no está habilitado: confirma la regla y guarda el plazo y los cambios del deporte.",
  EVENT_ACCESS_DENIED: "No tienes acceso a este evento.",
  SESSION_REQUIRED: "Tu sesión expiró. Vuelve a iniciar sesión.",
});

export const getRugbyErrorMessage = (error, fallback = "No se pudo completar la operación.") => {
  const data = error?.response?.data || {};
  if (data.code === "RULE_VIOLATION" && Array.isArray(data.details?.violations) && data.details.violations.length) {
    return `${ERROR_MESSAGES.RULE_VIOLATION} ${data.details.violations.map((item) => item.message).join(" ")}`;
  }
  return ERROR_MESSAGES[data.code] || data.message || data.msg || fallback;
};
